import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@img/ui';
import { authClient } from '../lib/auth-client';

/**
 * 👤 قائمة المستخدم المنسدلة (avatar + dropdown)
 *    تستخدم في PublicLayout و AuthLayout بنفس الشكل
 *    تتكيف حسب دور المستخدم:
 *      - USER: روابط شخصية فقط
 *      - BROKER: + لوحة المسوّق
 *      - ADMIN/SUPER_ADMIN: + لوحة الإدارة (بشارة ذهبية)
 */
export interface UserMenuUser {
  id: string;
  name: string;
  email?: string | null;
  image?: string | null;
  role?: string;
  verificationTier?: string | null;
}

export function UserMenu({
  user,
  variant = 'public', // public = للهيدر العام، dashboard = داخل لوحة التحكم
}: {
  user: UserMenuUser;
  variant?: 'public' | 'dashboard';
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // إقفال القائمة عند الضغط خارجها أو ESC
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const role = user.role ?? 'USER';
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const isBroker = role === 'BROKER';
  const tier = user.verificationTier && user.verificationTier !== 'NONE' ? user.verificationTier : null;

  const close = () => setOpen(false);
  const handleLogout = async () => {
    close();
    await authClient.signOut();
    navigate('/');
  };

  // 🎨 ألوان حسب الدور — لتمييز الأدمن بشكل لطيف
  const ringColor = isAdmin ? 'ring-gold' : isBroker ? 'ring-green-400' : 'ring-navy-light/40';
  const avatarBg = isAdmin
    ? 'bg-gradient-to-br from-gold-dark to-gold'
    : isBroker
    ? 'bg-gradient-to-br from-green-700 to-green-500'
    : 'bg-gradient-to-br from-navy to-navy-dark';

  return (
    <div className="relative" ref={ref}>
      {/* 🟢 زر الـ avatar */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 p-1 pl-2 rounded-full transition hover:bg-gray-100 ${
          open ? 'bg-gray-100' : ''
        }`}
        title={user.name}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Avatar user={user} size="sm" ringColor={ringColor} avatarBg={avatarBg} />
        <span className="hidden md:flex items-center gap-1 max-w-[100px]">
          <span className="text-xs font-bold text-ink-secondary truncate">
            {user.name.split(' ')[0]}
          </span>
          <span className="text-[9px] text-ink-muted">▼</span>
        </span>
      </button>

      {/* 📋 القائمة */}
      {open && (
        <div className={`absolute top-full mt-2 ${variant === 'public' ? 'left-0' : 'left-0'} w-72 bg-white rounded-2xl shadow-brand-xl border border-gray-100 overflow-hidden z-50`}>
          {/* 🎨 Header — يختلف حسب الدور */}
          <div className={`px-4 py-4 ${
            isAdmin
              ? 'bg-gradient-to-l from-navy-darker via-navy-dark to-gold/30'
              : isBroker
              ? 'bg-gradient-to-l from-green-700 to-green-600'
              : 'bg-gradient-to-l from-navy-dark to-navy'
          } text-white`}>
            <div className="flex items-center gap-3">
              <Avatar user={user} size="lg" ringColor="ring-white/30" avatarBg={avatarBg} />
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{user.name}</div>
                {user.email && (
                  <div className="text-[10px] text-white/70 truncate" dir="ltr">{user.email}</div>
                )}
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {isAdmin && <Badge color="gold">{role === 'SUPER_ADMIN' ? '👑 SUPER' : '⭐ ADMIN'}</Badge>}
                  {isBroker && <Badge color="green">🤝 BROKER</Badge>}
                  {!isAdmin && !isBroker && <Badge color="navy">USER</Badge>}
                  {tier && <Badge color="green">{tier}</Badge>}
                </div>
              </div>
            </div>
          </div>

          {/* 👑 لوحة الإدارة (للأدمن فقط — قسم بارز في الأعلى) */}
          {isAdmin && (
            <div className="border-b border-gray-100 p-2 bg-gold-soft/40">
              <Link
                to="/admin"
                onClick={close}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gradient-to-l from-navy-dark to-navy text-white font-bold text-sm hover:shadow-md transition"
              >
                <span className="flex items-center gap-2">
                  <span>🎯</span>
                  <span>{t('user_menu.admin_panel')}</span>
                </span>
                <span className="text-gold text-xs">←</span>
              </Link>
            </div>
          )}

          {/* 🤝 لوحة المسوّق (للمسوّقين) */}
          {isBroker && (
            <div className="border-b border-gray-100 p-2 bg-green-50/40">
              <Link
                to="/dashboard"
                onClick={close}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gradient-to-l from-green-700 to-green-600 text-white font-bold text-sm hover:shadow-md transition"
              >
                <span className="flex items-center gap-2">
                  <span>🤝</span>
                  <span>{t('user_menu.broker_dashboard')}</span>
                </span>
                <span className="text-green-200 text-xs">←</span>
              </Link>
            </div>
          )}

          {/* 🏠 الروابط الشخصية */}
          <div className="py-1">
            <MenuLink to="/dashboard" onClick={close} icon="🏠" label={t('user_menu.my_account')} />
            <MenuLink to="/dashboard/profile" onClick={close} icon="👤" label={t('user_menu.profile')} />
            <MenuDivider />
            <MenuLink to="/dashboard/properties" onClick={close} icon="🏘️" label={t('user_menu.my_properties')} />
            <MenuLink to="/dashboard/auctions" onClick={close} icon="🔨" label={t('user_menu.my_auctions')} />
            <MenuLink to="/dashboard/favorites" onClick={close} icon="❤️" label={t('user_menu.my_favorites')} />
            <MenuLink to="/dashboard/requests" onClick={close} icon="📋" label={t('user_menu.my_requests')} />
            <MenuDivider />
            <MenuLink to="/dashboard/wallet" onClick={close} icon="💳" label={t('user_menu.wallet')} />
            <MenuLink to="/dashboard/notifications" onClick={close} icon="🔔" label={t('user_menu.notifications')} />
            {isBroker && (
              <MenuLink to="/dashboard/subscription" onClick={close} icon="💎" label={t('user_menu.subscription')} />
            )}
          </div>

          {/* 🆘 الدعم الفني */}
          <div className="border-t border-gray-100 py-1 bg-gray-50/50">
            <MenuLink to="/contact" onClick={close} icon="🆘" label={t('user_menu.support')} />
          </div>

          {/* 🚪 تسجيل الخروج */}
          <div className="border-t border-gray-100 p-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl transition"
            >
              <span>↪</span>
              <span>{t('user_menu.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Sub Components ━━━━━━━━━━━━━━━━━━━━

function Avatar({ user, size, ringColor, avatarBg }: {
  user: UserMenuUser; size: 'sm' | 'lg'; ringColor: string; avatarBg: string;
}) {
  const sizeClass = size === 'sm' ? 'w-9 h-9 text-sm' : 'w-12 h-12 text-lg';
  if (user.image) {
    return (
      <img
        src={user.image}
        alt={user.name}
        className={`${sizeClass} rounded-full object-cover ring-2 ${ringColor} shrink-0`}
      />
    );
  }
  // ✨ avatar حرف أول (إذا ما في صورة)
  return (
    <div
      className={`${sizeClass} rounded-full ${avatarBg} text-white font-black flex items-center justify-center ring-2 ${ringColor} shrink-0 select-none`}
    >
      {user.name.charAt(0).toUpperCase()}
    </div>
  );
}

function MenuLink({ to, onClick, icon, label }: { to: string; onClick: () => void; icon: string; label: string }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2 text-sm font-bold text-ink hover:bg-gray-50 transition"
    >
      <span className="text-base w-5 text-center shrink-0">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
    </Link>
  );
}

function MenuDivider() {
  return <div className="my-1 border-t border-gray-100" />;
}
